import { useCallback, useEffect, useMemo, useState } from 'react';

import Fuse from 'fuse.js';
import { isEqual } from 'lodash';
import { useLocation, useParams } from 'react-router-dom';

import { SearchApi } from '../openapi/leo';
import { NeoLeoSearchResult } from '../utils/LeoUtils';
import { getLeoConfiguration } from '../utils/OpenapiConfigurationUtils';
import ErrorService from '../services/ErrorService';

const LIMIT = 100;
const PAGE = 1;
const ACTION_ITEMS_LIMIT = 5;

export enum NeoLeoSearchCategoryEnum {
  ExternalLinks = 'EXTERNAL_LINKS',
  InternalPages = 'INTERNAL_PAGES',
  PromptSuggestions = 'PROMPT_SUGGESTIONS',
  Deposits = 'DEPOSITS',
  Roar = 'ROAR',
  MARKETING_SIGN_REVIEW = 'MARKETING_SIGN_REVIEW',
}

const useNeoLeoKBarActions = (askLeoText?: string) => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [filteredActions, setFilteredActions] = useState<NeoLeoSearchResult[]>(
    [],
  );
  const [actions, setActions] = useState<NeoLeoSearchResult[] | undefined>(
    undefined,
  );

  const source = useMemo(() => {
    if (!id) {
      return;
    }

    const isTransactionDetailRoute = location.pathname.startsWith(
      `/transactions/${id}`,
    );

    if (isTransactionDetailRoute) {
      return `transactions/${id}`;
    }

    const isListingDetailRoute = location.pathname.startsWith(
      `/listings/${id}`,
    );

    if (isListingDetailRoute) {
      return `listings/${id}`;
    }

    return undefined;
  }, [id, location.pathname]);

  useEffect(() => {
    const fetchActions = async () => {
      try {
        const { data } = await new SearchApi(
          getLeoConfiguration(),
        ).searchSearchGet(undefined, source, PAGE, LIMIT);
        setActions(data?.results);
      } catch (e) {
        setActions([]);
        ErrorService.notifyIgnoreAuthErrors(
          'Unable to fetch neo leo actions',
          e as Error,
          {
            req: {
              source,
            },
          },
        );
      }
    };

    fetchActions();
  }, [source]);

  const getFilteredActions = useCallback(
    (searchTerm?: string) => {
      if (!actions?.length) {
        return;
      }

      const fuseOptions = {
        keys: ['title'], // Searching based on 'title' field
        threshold: 0.3, // Adjust this value for fuzziness (lower is stricter)
        distance: 100, // Adjust this value for fuzziness (higher is stricter)
      };

      const newFilteredActions: NeoLeoSearchResult[] = actions
        .map((category: NeoLeoSearchResult) => {
          const fuse = new Fuse(category.items, fuseOptions);

          const filteredItems = fuse
            .search(searchTerm || '')
            .map((result: any) => result.item);

          if (filteredItems.length > 0) {
            return {
              ...category,
              items: filteredItems.slice(0, ACTION_ITEMS_LIMIT),
            };
          }

          return null;
        })
        .filter((el): el is NeoLeoSearchResult => el !== null);

      if (!isEqual(newFilteredActions, filteredActions)) {
        setFilteredActions(newFilteredActions);
      }
    },
    [actions, filteredActions],
  );

  useEffect(() => {
    if (askLeoText) {
      getFilteredActions(askLeoText);
    }
  }, [askLeoText, getFilteredActions]);

  if (!askLeoText) {
    return actions || [];
  }

  return filteredActions;
};

export default useNeoLeoKBarActions;
